import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import config from 'config/config';

interface AuthUser {
  id: number;
  email: string;
  displayName: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  authFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'jwt_token';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(null);

  const fetchMe = useCallback(async (jwt: string) => {
    const res = await fetch(`${config.apiUrl}/api/auth/me`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (!res.ok) {
      setToken(null);
      setUser(null);
      localStorage.removeItem(TOKEN_KEY);
      return;
    }
    const data = await res.json();
    setUser({ id: data.id, email: data.email, displayName: data.displayName });
  }, []);

  useEffect(() => {
    if (token) {
      fetchMe(token);
    }
  }, [token, fetchMe]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${config.apiUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      throw new Error('Nieprawidłowy email lub hasło');
    }

    const data = await res.json();
    const jwt: string = data.token;

    localStorage.setItem(TOKEN_KEY, jwt);
    setToken(jwt);
    await fetchMe(jwt);
  }, [fetchMe]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const authFetch = useCallback(async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(input, {
      ...init,
      headers: {
        ...init?.headers,
        ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
      },
    });
    if (res.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
    }
    return res;
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isLoggedIn: !!token, login, logout, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
